import { useEffect, useMemo, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import { pickAssets } from './planetbuilderAssets.js';
import { countContinentReveals, deriveVisualFlags } from './planetVisualTriggers.js';
import { getPlanetConfig, PLANET_CONFIG_LAYOUT_VERSION, type PlanetConfig } from './planetConfig.js';

interface SurfaceItem {
  item_id: string;
  angle: number;
  altitude: number;
  layer: string;
  scale: number;
  upgrade?: { category: string; name: string } | null;
}

interface PlanetCanvasProps {
  stage: number;
  visualState?: {
    waterOpacity?: number;
    atmosphereOpacity?: number;
    waterColor?: number | string;
    atmosphereColor?: number | string;
    starColor?: number | string;
    [k: string]: unknown;
  };
  surfaceItems?: SurfaceItem[];
  eventHistory?: Array<{ type?: string; eventType?: string; severity?: string; day?: number; [k: string]: unknown }>;
  rotationSpeed?: number;
  planetConfig?: PlanetConfig | null;
  planetId?: string;
  orbitEpochMs?: number;
  dayDurationMs?: number;
  currentDay?: number;
}

const hash = (v: string) => v.split('').reduce((a, c) => (a * 33 + c.charCodeAt(0)) >>> 0, 5381);
const rng = (seed: number) => () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
const polarToXY = (cx: number, cy: number, r: number, angleDeg: number, radial: number) => ({
  x: cx + Math.cos((angleDeg * Math.PI) / 180) * r * radial,
  y: cy + Math.sin((angleDeg * Math.PI) / 180) * r * radial,
});
const surfaceItemToXY = (cx: number, cy: number, r: number, item: SurfaceItem) => {
  const angleRad = (item.angle * Math.PI) / 180;
  const altitude = clamp(Number(item.altitude || 0), -0.8, 0.8);
  const radial = clamp(0.42 + Math.abs(altitude) * 0.18, 0.36, 0.58);
  return {
    x: cx + Math.cos(angleRad) * r * radial,
    y: cy + Math.sin(angleRad) * r * radial * 0.68 + altitude * r * 0.28,
    radial,
    angleRad,
  };
};
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const colorValue = (value: unknown, fallback: number) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(/^#/, '');
    const parsed = Number.parseInt(normalized, 16);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};
const mixColor = (a: number, b: number, amount: number) => {
  const t = clamp(amount, 0, 1);
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.round(ar + (br - ar) * t) << 16) |
    (Math.round(ag + (bg - ag) * t) << 8) |
    Math.round(ab + (bb - ab) * t)
  );
};
const visualScale = (base: number, radial: number) => {
  const modernScale = base < 0.5 ? base * 2.9 : base;
  return clamp(modernScale * (1 - radial * 0.18), 0.62, 1.05);
};
const stampSize = (r: number, baseScale: number, kind: 'biome' | 'development' | 'road' | 'infrastructure') => {
  const multipliers = {
    biome: 0.9,
    development: 1.05,
    road: 1.35,
    infrastructure: 1.0,
  };
  return r * baseScale * multipliers[kind];
};
const surfaceForeshortening = (radial: number) => clamp(1 - radial * 0.45, 0.68, 0.9);
const VISUAL_DAY_MS = 1000 * 60 * 60;
const mappedAssetKind = (category: string): 'biome' | 'development' | 'road' | 'infrastructure' => {
  if (category === 'biome') return 'biome';
  if (category === 'road') return 'road';
  if (category === 'infrastructure') return 'infrastructure';
  return 'development';
};
type RouteMover = {
  sp: PIXI.DisplayObject;
  points: PIXI.Point[];
  segmentLengths: number[];
  totalLength: number;
  t: number;
  speed: number;
};
const routeMetrics = (points: PIXI.Point[]) => {
  const segmentLengths = points.slice(1).map((point, index) => {
    const prev = points[index];
    return Math.hypot(point.x - prev.x, point.y - prev.y);
  });
  const totalLength = segmentLengths.reduce((sum, length) => sum + length, 0);
  return { segmentLengths, totalLength };
};
const pointAlongRoute = (route: RouteMover, progress: number) => {
  if (route.points.length === 0 || route.totalLength <= 0) return new PIXI.Point(0, 0);
  const wrapped = ((progress % 1) + 1) % 1;
  let remaining = wrapped * route.totalLength;
  for (let i = 0; i < route.segmentLengths.length; i++) {
    const length = route.segmentLengths[i];
    if (remaining <= length || i === route.segmentLengths.length - 1) {
      const a = route.points[i];
      const b = route.points[i + 1];
      const local = length <= 0 ? 0 : remaining / length;
      return new PIXI.Point(a.x + (b.x - a.x) * local, a.y + (b.y - a.y) * local);
    }
    remaining -= length;
  }
  return route.points[route.points.length - 1];
};
const angleDelta = (from: number, to: number) => {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
};

function getEventKinds(events: Array<{ type?: string; eventType?: string; severity?: string; [k: string]: unknown }>) {
  const text = events.map((e) => `${String(e.type || e.eventType || '')} ${String(e.name || '')} ${String(e.message || '')}`).join(' ').toLowerCase();
  return {
    solarFlare: /solar|flare/.test(text),
    earthquake: /quake|earthquake|tectonic/.test(text),
    tsunami: /tsunami|flood|tidal/.test(text),
    comet: /comet|impact|meteor/.test(text),
    storm: /storm|rain|cyclone|hurricane/.test(text),
    pandemic: /pandemic|disease|illness/.test(text),
  };
}

/**
 * Create a sprite from an image path, with fallback generated graphics if the image fails to load.
 * The fallback is automatically removed when the texture loads successfully.
 */
function textureSprite(path: string, fallbackColor: number, maxSize: number) {
  const t = PIXI.Texture.from(path);
  const sp = new PIXI.Sprite(t);
  sp.anchor.set(0.5);

  const fitLoadedTexture = () => {
    if (sp.destroyed || !sp.scale) return;
    if (!t.baseTexture.valid) return;
    const texWidth = t.width || t.baseTexture.width;
    const texHeight = t.height || t.baseTexture.height;
    if (!texWidth || !texHeight) return;
    sp.scale.set(maxSize / Math.max(texWidth, texHeight));
  };
  fitLoadedTexture();

  // Fallback if texture not loaded yet (async) or failed (404)
  if (!t.baseTexture.valid) {
    const g = new PIXI.Graphics();
    g.name = 'fallback';
    g.beginFill(fallbackColor, 0.58);
    g.drawCircle(0, 0, maxSize * 0.24);
    g.endFill();
    g.lineStyle(1, 0xffffff, 0.35);
    g.drawCircle(0, 0, maxSize * 0.24);
    sp.addChild(g);

    let pollId: number | undefined;
    let fallbackRemoved = false;
    const destroyFallback = () => {
      if (!g.destroyed) {
        g.destroy();
      }
    };
    const removeFallback = () => {
      if (fallbackRemoved) return;
      fallbackRemoved = true;
      if (pollId !== undefined) {
        window.clearInterval(pollId);
        pollId = undefined;
      }
      if (sp.destroyed) {
        destroyFallback();
        return;
      }
      fitLoadedTexture();
      if (g.parent === sp) sp.removeChild(g);
      destroyFallback();
    };

    t.baseTexture.once('loaded', removeFallback);
    t.baseTexture.once('error', () => {
      if (pollId !== undefined) {
        window.clearInterval(pollId);
        pollId = undefined;
      }
      // On error: keep fallback, but clean up the loaded listener
      t.baseTexture.off('loaded', removeFallback);
    });
    pollId = window.setInterval(() => {
      if (t.baseTexture.valid) removeFallback();
    }, 50);
  }
  return sp;
}

/**
 * Eagerly start loading textures so they're ready before rendering.
 * No await needed — PIXI.Texture.from caches and loads in background.
 */
function warmTextures(stage: number, config: PlanetConfig, surfaceItems: SurfaceItem[]) {
  const paths = new Set<string>();

  const planetBases = pickAssets(0, 'planet');
  if (planetBases.length > 0) paths.add(planetBases[config.baseIndex % planetBases.length].path);

  const continentAssets = pickAssets(1, 'continent');
  for (const contCfg of config.continents) {
    if (continentAssets.length > 0) paths.add(continentAssets[contCfg.assetIndex % continentAssets.length].path);
  }

  pickAssets(stage, 'biome').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'geology').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'water').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'road').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'infrastructure').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'movement').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'boat').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'plane').forEach((a) => paths.add(a.path));
  pickAssets(0, 'asteroid').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'satellite').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'overlay').forEach((a) => paths.add(a.path));
  pickAssets(stage, 'rocket').forEach((a) => paths.add(a.path));

  surfaceItems.forEach((item) => {
    const mapped = mapUpgradeToAsset(item.upgrade?.name || '', item.upgrade?.category, stage);
    if (!mapped) return;
    const assets = pickMappedAssets(mapped);
    if (assets.length > 0) paths.add(assets[mapped.variant % assets.length].path);
  });

  for (const path of paths) {
    PIXI.Texture.from(path); // starts background load, cached for later use
  }
}

/**
 * Map upgrade name/category to asset category + stage for rendering.
 */
export function mapUpgradeToAsset(name: string, category: string | undefined, _stage: number) {
  // Direct name mappings
  const nameMap: Record<string, { category: string; stage: number; variant: number; idPrefix?: string }> = {
    'Crust Stabilisation': { category: 'geology', stage: 0, variant: 0, idPrefix: 'crust_stabilisation' },
    'Volcanic Activity': { category: 'geology', stage: 0, variant: 0, idPrefix: 'volcanic_activity' },
    'Mineral Deposit': { category: 'geology', stage: 0, variant: 0, idPrefix: 'mineral_deposit' },
    'Geothermal Energy': { category: 'geology', stage: 0, variant: 0, idPrefix: 'geothermal_vent' },
    'Subsurface Ice': { category: 'water', stage: 0, variant: 0, idPrefix: 'subsurface_ice' },
    'Outgassing Event': { category: 'atmosphere', stage: 0, variant: 0 },
    'Coastal Land': { category: 'continent', stage: 1, variant: 0 },
    'Continent': { category: 'continent', stage: 2, variant: 1 },
    'Seed Atmosphere': { category: 'atmosphere', stage: 1, variant: 0 },
    'Water Ice Comet': { category: 'water', stage: 1, variant: 0, idPrefix: 'subsurface_ice' },
    'Ocean Patch': { category: 'water', stage: 1, variant: 0, idPrefix: 'ocean_patch' },
    'Hot Spring': { category: 'water', stage: 1, variant: 0, idPrefix: 'hot_spring' },
    'Hydrothermal Vent': { category: 'water', stage: 1, variant: 0, idPrefix: 'hydrothermal_vent' },
    'Temperature Stabilisation': { category: 'atmosphere', stage: 1, variant: 1 },
    'Magnetic Shield': { category: 'atmosphere', stage: 1, variant: 2 },
    'Primordial Soup': { category: 'life', stage: 1, variant: 0 },
    'Hydrothermal Vent Life': { category: 'life', stage: 2, variant: 1 },
    'Microbial Mat': { category: 'life', stage: 2, variant: 2 },
    'Photosynthetic Microbe': { category: 'life', stage: 2, variant: 3 },
    'Simple Cell': { category: 'life', stage: 2, variant: 4 },
    'Early Oxygen Production': { category: 'life', stage: 2, variant: 5 },
    'Shallow Water Life Zone': { category: 'life', stage: 2, variant: 6 },
    'Algae Bloom': { category: 'biome', stage: 3, variant: 0 },
    'Moss and Lichen': { category: 'biome', stage: 3, variant: 1 },
    'Simple Plants': { category: 'biome', stage: 3, variant: 2 },
    'Forest Patch': { category: 'biome', stage: 3, variant: 3 },
    'Soil Formation': { category: 'biome', stage: 3, variant: 4 },
    'Rain Cycle': { category: 'atmosphere', stage: 3, variant: 3 },
    'Decomposer Network': { category: 'biome', stage: 3, variant: 5 },
    'Small Creature': { category: 'life', stage: 4, variant: 7 },
    'Ocean Creature': { category: 'life', stage: 4, variant: 8 },
    'Habitat Diversity': { category: 'biome', stage: 4, variant: 6 },
    'Early Tribe': { category: 'development', stage: 2, variant: 0, idPrefix: 'huts_cluster' },
    'Early Settlement': { category: 'development', stage: 2, variant: 3, idPrefix: 'huts_cluster' },
    'Migration': { category: 'development', stage: 2, variant: 5, idPrefix: 'huts_cluster' },
    'Settlement Trading': { category: 'infrastructure', stage: 5, variant: 0, idPrefix: 'port' },
    'Farm': { category: 'development', stage: 1, variant: 0, idPrefix: 'farm_patch' },
    'Village': { category: 'development', stage: 3, variant: 0, idPrefix: 'village_cluster' },
    'Land Reclamation': { category: 'continent', stage: 2, variant: 1 },
    'Town': { category: 'development', stage: 4, variant: 0, idPrefix: 'town_cluster' },
    'City': { category: 'development', stage: 5, variant: 0, idPrefix: 'city_cluster' },
    'Basic Engineering': { category: 'road', stage: 3, variant: 0 },
    'Water System': { category: 'infrastructure', stage: 5, variant: 0, idPrefix: 'port' },
    'School': { category: 'development', stage: 3, variant: 1, idPrefix: 'village_cluster' },
    'Power Plant': { category: 'infrastructure', stage: 5, variant: 0, idPrefix: 'power_plant' },
    'Research Centre': { category: 'development', stage: 5, variant: 4, idPrefix: 'city_cluster' },
    'Factory': { category: 'infrastructure', stage: 5, variant: 0, idPrefix: 'industry' },
    'Hospital': { category: 'development', stage: 5, variant: 3, idPrefix: 'city_cluster' },
    'Road Network': { category: 'road', stage: 5, variant: 1 },
    'Radio Tower': { category: 'infrastructure', stage: 6, variant: 1, idPrefix: 'satellite_dish' },
    'Study Parent Star': { category: 'infrastructure', stage: 6, variant: 2, idPrefix: 'satellite_dish' },
    'Telescope': { category: 'infrastructure', stage: 6, variant: 3, idPrefix: 'satellite_dish' },
    'Satellite': { category: 'satellite', stage: 6, variant: 0 },
    'Signal Array': { category: 'infrastructure', stage: 6, variant: 1, idPrefix: 'satellite_dish' },
    'Orbital Telescope': { category: 'satellite', stage: 6, variant: 0 },
    'Launch Complex': { category: 'infrastructure', stage: 6, variant: 0, idPrefix: 'airport' },
    'Space Port': { category: 'infrastructure', stage: 6, variant: 0, idPrefix: 'port' },
    'Rocket Program': { category: 'rocket', stage: 6, variant: 0, idPrefix: 'rocket' },
    'Orbital Dock': { category: 'satellite', stage: 6, variant: 0, idPrefix: 'orbital_dock' },
    'Deep Space Probe': { category: 'satellite', stage: 6, variant: 3 },
    'Quantum Lab': { category: 'infrastructure', stage: 6, variant: 4, idPrefix: 'satellite_dish' },
    'Quantum Drive': { category: 'satellite', stage: 6, variant: 4 },
    'Planetary Defence Grid': { category: 'infrastructure', stage: 6, variant: 0, idPrefix: 'planetary_defence_grid' },
    'Orbital Defence Platform': { category: 'satellite', stage: 6, variant: 5 },
    'High-Orbit Shield Satellites': { category: 'satellite', stage: 6, variant: 6 },
    'Clean Energy': { category: 'infrastructure', stage: 5, variant: 0, idPrefix: 'wind_turbine' },
    'Carbon Capture': { category: 'infrastructure', stage: 5, variant: 0, idPrefix: 'industry' },
  };

  const mapped = nameMap[name];
  if (mapped) return mapped;

  // Category fallbacks
  if (category === 'geology') return { category: 'geology', stage: 0, variant: hash(name) };
  if (category === 'water') return { category: 'water', stage: 1, variant: hash(name) };
  if (category === 'atmosphere') return { category: 'atmosphere', stage: 1, variant: hash(name) };
  if (category === 'life') return { category: 'life', stage: 2, variant: hash(name) };
  if (category === 'plant') return { category: 'biome', stage: 3, variant: hash(name) };
  if (category === 'civilisation') return { category: 'development', stage: 5, variant: hash(name) };
  if (category === 'technology') return { category: 'infrastructure', stage: 6, variant: hash(name) };

  return null;
}

export function pickMappedAssets(mapped: ReturnType<typeof mapUpgradeToAsset>) {
  if (!mapped) return [];
  const assets = pickAssets(mapped.stage, mapped.category);
  const prefix = mapped.idPrefix;
  if (!prefix) return assets;
  return assets.filter((asset) => asset.id.startsWith(prefix));
}

export default function PlanetCanvas({
  stage,
  visualState,
  surfaceItems = [],
  eventHistory = [],
  rotationSpeed = 0.00012,
  planetConfig: externalConfig,
  planetId,
  orbitEpochMs,
  dayDurationMs,
  currentDay = 0,
}: PlanetCanvasProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const visibleSurfaceItems = useMemo(
    () => surfaceItems.filter((item) => (item as SurfaceItem & { visible?: boolean }).visible !== false),
    [surfaceItems],
  );
  const surfaceItemsKey = useMemo(
    () => visibleSurfaceItems
      .map((item) => [
        item.item_id,
        item.angle,
        item.altitude,
        item.layer,
        item.scale,
        item.upgrade?.category || '',
        item.upgrade?.name || '',
      ].join(':'))
      .join('|'),
    [visibleSurfaceItems],
  );
  const eventHistoryKey = useMemo(
    () => eventHistory
      .map((event) => [
        String(event.type || ''),
        String(event.eventType || ''),
        String(event.severity || ''),
        String(event.name || ''),
        String(event.message || ''),
      ].join(':'))
      .join('|'),
    [eventHistory],
  );
  const externalConfigKey = useMemo(
    () => externalConfig ? JSON.stringify(externalConfig) : '',
    [externalConfig],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateSize = () => {
      const width = Math.max(1, Math.round(element.clientWidth || 0));
      const height = Math.max(1, Math.round(element.clientHeight || 0));
      setCanvasSize((current) => (
        current.width === width && current.height === height ? current : { width, height }
      ));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    window.addEventListener('orientationchange', updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', updateSize);
    };
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const w = canvasSize.width || ref.current.clientWidth || 700;
    const h = canvasSize.height || ref.current.clientHeight || 700;
    if (w <= 1 || h <= 1) return;
    const r = Math.min(w, h) * (w < 640 ? 0.38 : 0.34);

    const app = new PIXI.Application({
      width: w,
      height: h,
      antialias: true,
      backgroundAlpha: 0,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    ref.current.appendChild(app.view as HTMLCanvasElement);
    const view = app.view as HTMLCanvasElement;
    view.style.position = 'absolute';
    view.style.inset = '0';
    view.style.width = '100%';
    view.style.height = '100%';
    view.style.zIndex = '1';

    const cx = w / 2;
    const cy = h / 2;
    const seedSource = externalConfig?.seed || localStorage.getItem('dev_user_id') || 'planet_default';
    const epochKey = `planet_orbit_epoch_${planetId || seedSource}`;
    let stableEpochMs = typeof orbitEpochMs === 'number' && Number.isFinite(orbitEpochMs)
      ? orbitEpochMs
      : Number(localStorage.getItem(epochKey));
    if (!Number.isFinite(stableEpochMs) || stableEpochMs <= 0) {
      stableEpochMs = Date.now();
      localStorage.setItem(epochKey, String(stableEpochMs));
    }
    const visualCycleMs = VISUAL_DAY_MS * 6;
    const starColor = colorValue(visualState?.starColor, 0xfff0c4);

    const starLayer = new PIXI.Container();
    const dayStarBackLayer = new PIXI.Container();
    const starSeeded = rng(hash(seedSource + '_stars'));
    const stars: { g: PIXI.Graphics; base: number; phase: number; speed: number }[] = [];
    const starCount = clamp(Math.floor((w * h) / 9500), 55, 130);
    for (let i = 0; i < starCount; i++) {
      const star = new PIXI.Graphics();
      const size = 0.55 + starSeeded() * 1.35;
      star.beginFill(0xdcecff, 0.9);
      star.drawCircle(0, 0, size);
      star.endFill();
      star.x = starSeeded() * w;
      star.y = starSeeded() * h;
      star.alpha = 0.08 + starSeeded() * 0.32;
      starLayer.addChild(star);
      stars.push({
        g: star,
        base: 0.1 + starSeeded() * 0.28,
        phase: starSeeded() * Math.PI * 2,
        speed: 0.00022 + starSeeded() * 0.00032,
      });
    }
    app.stage.addChild(starLayer);
    app.stage.addChild(dayStarBackLayer);

    const driftingStar = new PIXI.Container();
    const corona = new PIXI.Graphics();
    corona.blendMode = PIXI.BLEND_MODES.ADD;
    [
      [0.34, 0.018],
      [0.25, 0.035],
      [0.17, 0.06],
      [0.1, 0.12],
    ].forEach(([scale, alpha]) => {
      corona.beginFill(starColor, alpha);
      corona.drawCircle(0, 0, r * scale);
      corona.endFill();
    });
    const starCore = new PIXI.Graphics();
    starCore.blendMode = PIXI.BLEND_MODES.ADD;
    starCore.beginFill(starColor, 0.82);
    starCore.drawCircle(0, 0, r * 0.044);
    starCore.endFill();
    starCore.beginFill(0xffffff, 0.55);
    starCore.drawCircle(-r * 0.01, -r * 0.012, r * 0.024);
    starCore.endFill();
    for (let i = 0; i < 8; i++) {
      const mote = new PIXI.Graphics();
      const moteAngle = starSeeded() * Math.PI * 2;
      const moteDist = starSeeded() * r * 0.035;
      mote.beginFill(i % 2 === 0 ? 0xffffff : starColor, 0.12 + starSeeded() * 0.16);
      mote.drawEllipse(0, 0, r * (0.006 + starSeeded() * 0.006), r * (0.003 + starSeeded() * 0.006));
      mote.endFill();
      mote.x = Math.cos(moteAngle) * moteDist;
      mote.y = Math.sin(moteAngle) * moteDist;
      mote.rotation = moteAngle;
      starCore.addChild(mote);
    }
    const softFlare = new PIXI.Graphics();
    softFlare.blendMode = PIXI.BLEND_MODES.ADD;
    softFlare.lineStyle(1.2, starColor, 0.08);
    softFlare.moveTo(-r * 0.18, 0);
    softFlare.lineTo(r * 0.18, 0);
    softFlare.lineStyle(0.8, 0xffffff, 0.045);
    softFlare.moveTo(0, -r * 0.09);
    softFlare.lineTo(0, r * 0.09);
    driftingStar.addChild(corona, softFlare, starCore);
    dayStarBackLayer.addChild(driftingStar);

    // Layer hierarchy
    const planetRoot = new PIXI.Container();
    const asteroidBackLayer = new PIXI.Container();
    const planetContainer = new PIXI.Container();
    const asteroidFrontLayer = new PIXI.Container();
    planetContainer.pivot.set(cx, cy);
    planetContainer.position.set(cx, cy);
    planetContainer.rotation = (((Date.now() - stableEpochMs) / (1000 / 60)) * rotationSpeed) % (Math.PI * 2);

    const basePlanetLayer = new PIXI.Container();
    const geologyLayer = new PIXI.Container();
    const waterLayer = new PIXI.Container();
    const continentLayer = new PIXI.Container();
    const biomeLayer = new PIXI.Container();
    const developmentLayer = new PIXI.Container();
    const roadLayer = new PIXI.Container();
    const infrastructureLayer = new PIXI.Container();
    const routeLayer = new PIXI.Container();
    const movementLayer = new PIXI.Container();
    const lightsLayer = new PIXI.Container();
    const cloudLayer = new PIXI.Container();
    const atmosphereLayer = new PIXI.Container();
    cloudLayer.pivot.set(cx, cy);
    cloudLayer.position.set(cx, cy);

    planetRoot.addChild(asteroidBackLayer, planetContainer, asteroidFrontLayer);
    planetContainer.addChild(
      basePlanetLayer,
      geologyLayer,
      waterLayer,
      continentLayer,
      biomeLayer,
      developmentLayer,
      roadLayer,
      infrastructureLayer,
      routeLayer,
      movementLayer,
      lightsLayer,
      cloudLayer,
      atmosphereLayer,
    );
    app.stage.addChild(planetRoot);

    const frontLightLayer = new PIXI.Container();
    const frontLight = new PIXI.Graphics();
    frontLight.beginFill(starColor, 0.18);
    frontLight.drawCircle(0, 0, r * 0.72);
    frontLight.endFill();
    frontLight.beginFill(0xffffff, 0.08);
    frontLight.drawCircle(-r * 0.12, -r * 0.08, r * 0.34);
    frontLight.endFill();
    frontLight.blendMode = PIXI.BLEND_MODES.ADD;
    frontLightLayer.addChild(frontLight);
    frontLightLayer.visible = false;
    const frontLightMask = new PIXI.Graphics();
    frontLightMask.beginFill(0xffffff);
    frontLightMask.drawCircle(cx, cy, r);
    frontLightMask.endFill();
    frontLightMask.renderable = false;
    frontLightLayer.mask = frontLightMask;
    app.stage.addChild(frontLightLayer);
    app.stage.addChild(frontLightMask);

    // Derive flags from purchased items + events
    const purchasedNames = visibleSurfaceItems.map((x) => x.upgrade?.name).filter(Boolean) as string[];
    const purchasedCategories = visibleSurfaceItems.map((x) => x.upgrade?.category).filter(Boolean) as string[];
    const {
      hasContinent,
      hasLandmass,
      hasRoadNetwork,
      hasRoadNetworkUpgrade,
      hasEarlySettlement,
      hasSettlementTrading,
      hasCity,
      hasInfrastructure,
      hasWaterUnlock,
      hasSatellite,
      hasSpacePort,
      hasLaunchTower,
      hasRocket,
      hasOrbitalDock,
      hasProbe,
      hasDefenceSatellites,
      hasShieldSatellites,
      hasOceanRipples,
      hasMeteorImpact,
      hasSolarFlare,
      hasPandemic,
      hasPollutionBloom,
    } =
      deriveVisualFlags(purchasedNames, purchasedCategories, eventHistory, currentDay);

    const kinds = getEventKinds(eventHistory);

    // Planet config (persistent per-player variation)
    const generatedConfig = getPlanetConfig(seedSource, stage);
    const externalContinents = externalConfig?.continents || [];
    const externalLayoutVersion = Number(externalConfig?.layoutVersion || 0);
    const config = externalConfig
      ? {
          ...generatedConfig,
          ...externalConfig,
          layoutVersion: PLANET_CONFIG_LAYOUT_VERSION,
          continents: (hasContinent || hasLandmass) && (externalLayoutVersion < PLANET_CONFIG_LAYOUT_VERSION || externalContinents.length < 3)
            ? generatedConfig.continents
            : externalContinents,
        }
      : generatedConfig;
    const seeded = rng(hash(seedSource + '_canvas'));

    // Start warming textures in background before we create sprites
    warmTextures(stage, config, visibleSurfaceItems);

    const palette = {
      ...config.palette,
      waterColor: colorValue(visualState?.waterColor, config.palette.waterColor),
      atmosphereColor: colorValue(visualState?.atmosphereColor, config.palette.atmosphereColor),
    };
    const rawWaterOpacity = visualState?.waterOpacity == null
      ? (stage >= 1 || hasWaterUnlock ? 0.36 : 0)
      : Number(visualState.waterOpacity);
    const rawAtmosphereOpacity = visualState?.atmosphereOpacity == null
      ? (stage >= 1 || hasWaterUnlock ? 0.18 : 0)
      : Number(visualState.atmosphereOpacity);
    const waterOpacity = clamp(rawWaterOpacity, 0, 0.58);
    const atmOpacity = clamp(rawAtmosphereOpacity, 0, 0.55);

    // === BASE PLANET ===
    const planetBases = pickAssets(0, 'planet');
    const solidPlanet = new PIXI.Graphics();
    solidPlanet.beginFill(palette.baseTint);
    solidPlanet.drawCircle(cx, cy, r * 0.99);
    solidPlanet.endFill();
    basePlanetLayer.addChild(solidPlanet);

    if (planetBases.length > 0) {
      const baseAsset = planetBases[config.baseIndex % planetBases.length];
      const baseSprite = textureSprite(baseAsset.path, palette.baseTint, r * 2.05);
      baseSprite.x = cx;
      baseSprite.y = cy;
      baseSprite.tint = palette.baseTint;
      baseSprite.alpha = 0.9;
      basePlanetLayer.addChild(baseSprite);
    }

    for (let i = 0; i < 12; i++) {
      const angle = seeded() * Math.PI * 2;
      const radial = Math.sqrt(seeded()) * 0.72;
      const mark = new PIXI.Graphics();
      mark.beginFill(i % 3 === 0 ? 0x3d3329 : 0x9b876d, i % 3 === 0 ? 0.22 : 0.16);
      mark.drawEllipse(0, 0, r * (0.018 + seeded() * 0.035), r * (0.012 + seeded() * 0.022));
      mark.endFill();
      mark.x = cx + Math.cos(angle) * r * radial;
      mark.y = cy + Math.sin(angle) * r * radial;
      mark.rotation = angle + seeded() * 0.8;
      basePlanetLayer.addChild(mark);
    }

    // === WATER ===
    const rippleBands: PIXI.Graphics[] = [];
    const waterFlowLines: PIXI.Graphics[] = [];
    if ((stage >= 1 || hasWaterUnlock) && waterOpacity > 0.03) {
      const waterHighlight = mixColor(palette.waterColor, 0xdff8ff, 0.55);
      const waterMidline = mixColor(palette.waterColor, 0x67c7e7, 0.3);
      const waterShadow = mixColor(palette.waterColor, 0x002947, 0.45);
      const water = new PIXI.Graphics();
      water.beginFill(palette.waterColor, hasPollutionBloom ? waterOpacity * 1.05 : waterOpacity * 1.12);
      water.drawCircle(cx, cy, r * 0.97);
      water.endFill();
      water.lineStyle(1.4, waterHighlight, waterOpacity * 0.18);
      water.drawCircle(cx, cy, r * 0.955);
      if (hasPollutionBloom) {
        water.beginFill(0x7d8b38, waterOpacity * 0.16);
        water.drawCircle(cx + r * 0.18, cy + r * 0.1, r * 0.34);
        water.endFill();
      }
      waterLayer.addChild(water);

      for (let i = 0; i < 7; i++) {
        const line = new PIXI.Graphics();
        const y = cy + r * (-0.42 + i * 0.14);
        const widthScale = 0.44 + (i % 3) * 0.09;
        line.lineStyle(1.1, i % 2 === 0 ? waterHighlight : waterMidline, waterOpacity * (0.11 + (i % 2) * 0.035));
        line.moveTo(cx - r * widthScale, y);
        line.bezierCurveTo(
          cx - r * widthScale * 0.35,
          y - r * 0.035,
          cx + r * widthScale * 0.22,
          y + r * 0.04,
          cx + r * widthScale,
          y,
        );
        line.rotation = -0.12 + i * 0.035;
        waterFlowLines.push(line);
        waterLayer.addChild(line);
      }

      if (hasOceanRipples) {
        for (let i = 0; i < 5; i++) {
          // Draw at the origin and position at the planet centre so rotation and
          // scale pulse around the planet — rotating an ellipse drawn at (cx, cy)
          // would swing it around the canvas corner and across the planet face.
          const band = new PIXI.Graphics();
          band.lineStyle(1.2, i % 2 === 0 ? waterHighlight : waterShadow, waterOpacity * (0.16 - i * 0.014));
          band.drawEllipse(0, 0, r * (0.42 + i * 0.11), r * (0.12 + i * 0.035));
          band.position.set(cx, cy);
          band.rotation = -0.45 + i * 0.22;
          rippleBands.push(band);
          waterLayer.addChild(band);
        }
      }

      if (kinds.storm) {
        const stormTint = new PIXI.Graphics();
        stormTint.beginFill(0x233a56, waterOpacity * 0.2);
        stormTint.drawCircle(cx, cy, r * 0.96);
        stormTint.endFill();
        waterLayer.addChild(stormTint);
      }
    }

    // === CIRCULAR MASK for planet-attached layers ===
    const planetMask = new PIXI.Graphics();
    planetMask.beginFill(0xffffff);
    planetMask.drawCircle(cx, cy, r);
    planetMask.endFill();
    basePlanetLayer.mask = planetMask;
    geologyLayer.mask = planetMask;
    waterLayer.mask = planetMask;
    continentLayer.mask = planetMask;
    biomeLayer.mask = planetMask;
    developmentLayer.mask = planetMask;
    roadLayer.mask = planetMask;
    infrastructureLayer.mask = planetMask;
    routeLayer.mask = planetMask;
    movementLayer.mask = planetMask;
    lightsLayer.mask = planetMask;
    cloudLayer.mask = planetMask;
    app.stage.addChild(planetMask);

    // === CONTINENTS with per-continent local slot containers ===
    const contRoots: PIXI.Container[] = [];
    const dockPoints: PIXI.Point[] = [];
    const airportPoints: PIXI.Point[] = [];
    const continentAssets = pickAssets(1, 'continent');

    // Continents reveal progressively: starter land work (Coastal Land) shows a
    // single small landmass; each Continent / Land Reclamation completion
    // reveals another full continent. Previously one Coastal Land purchase
    // rendered every configured continent, making the later Continent research
    // appear to do nothing.
    const continentRevealCount = Math.min(
      config.continents.length,
      Math.max(hasLandmass ? 1 : 0, countContinentReveals(purchasedNames)),
    );
    const revealedContinents = config.continents.slice(0, continentRevealCount);
    const starterScale = hasContinent ? 1 : 0.6;

    if ((hasContinent || hasLandmass) && continentAssets.length > 0 && revealedContinents.length > 0) {
      for (const contCfg of revealedContinents) {
        const asset = continentAssets[contCfg.assetIndex % continentAssets.length];
        const renderRadial = clamp(contCfg.radial < 0.46 ? contCfg.radial + 0.08 : contCfg.radial, 0.42, 0.58);
        const p = polarToXY(cx, cy, r, contCfg.angle, renderRadial);
        const dx = p.x - cx;
        const dy = p.y - cy;
        const len = Math.max(1, Math.hypot(dx, dy));
        const nx = dx / len;
        const ny = dy / len;

        // Continent container with local layers
        const contContainer = new PIXI.Container();
        contContainer.x = p.x;
        contContainer.y = p.y;
        const angleRad = (contCfg.angle * Math.PI) / 180;
        contContainer.rotation = angleRad + Math.PI / 2 + contCfg.rotation * 0.35;
        const s = visualScale(contCfg.scale, renderRadial) * starterScale;
        contContainer.scale.set(s, s * surfaceForeshortening(renderRadial));

        // Local layers (children of continent, inherit transform)
        const localBiome = new PIXI.Container();
        const localDev = new PIXI.Container();
        const localRoad = new PIXI.Container();
        const localInfra = new PIXI.Container();
        const localLights = new PIXI.Container();
        contContainer.addChild(localBiome, localDev, localRoad, localInfra, localLights);

        // Continent sprite
        const sprite = textureSprite(asset.path, palette.continentTint, r * 0.68);
        if (asset.canTint) sprite.tint = palette.continentTint;
        contContainer.addChildAt(sprite, 0); // behind local layers

        continentLayer.addChild(contContainer);
        contRoots.push(contContainer);
        dockPoints.push(new PIXI.Point(
          cx + nx * r * clamp(renderRadial + 0.24, 0.64, 0.84),
          cy + ny * r * clamp(renderRadial + 0.24, 0.64, 0.84),
        ));
        airportPoints.push(new PIXI.Point(p.x - nx * r * 0.04, p.y - ny * r * 0.04));
      }
    } else if (hasContinent || hasLandmass) {
      // Fallback procedural continents
      const count = Math.max(1, Math.min(5, continentRevealCount));
      const slots = [
        { angle: 25, radial: 0.38 },
        { angle: 85, radial: 0.46 },
        { angle: 145, radial: 0.5 },
        { angle: 215, radial: 0.42 },
        { angle: 285, radial: 0.54 },
      ]
        .sort(() => seeded() - 0.5)
        .slice(0, count);
      for (const s of slots) {
        const p = polarToXY(cx, cy, r, s.angle, s.radial);
        const dx = p.x - cx;
        const dy = p.y - cy;
        const len = Math.max(1, Math.hypot(dx, dy));
        const nx = dx / len;
        const ny = dy / len;
        const c = new PIXI.Container();
        c.x = p.x;
        c.y = p.y;
        const angleRad = (s.angle * Math.PI) / 180;
        c.rotation = angleRad + Math.PI / 2 + (seeded() - 0.5) * 0.2;
        const scale = visualScale(0.9, s.radial);
        c.scale.set(scale, scale * surfaceForeshortening(s.radial));
        const edge = new PIXI.Graphics();
        edge.beginFill(0x564737);
        edge.drawEllipse(0, 0, r * 0.29, r * 0.17);
        edge.endFill();
        const land = new PIXI.Graphics();
        land.beginFill(0x93b66f);
        land.drawEllipse(0, -2, r * 0.25, r * 0.14);
        land.endFill();
        c.addChild(edge, land);
        continentLayer.addChild(c);
        contRoots.push(c);
        dockPoints.push(new PIXI.Point(
          cx + nx * r * clamp(s.radial + 0.24, 0.64, 0.84),
          cy + ny * r * clamp(s.radial + 0.24, 0.64, 0.84),
        ));
        airportPoints.push(new PIXI.Point(p.x - nx * r * 0.04, p.y - ny * r * 0.04));
      }
    }

    // === BIOMES (placed on continents) ===
    const biomeAssets = pickAssets(stage, 'biome');
    contRoots.forEach((cont, ci) => {
      if (biomeAssets.length === 0) return;
      // 2-3 biomes per continent
      const biomeCount = Math.min(3, biomeAssets.length);
      for (let i = 0; i < biomeCount; i++) {
        const asset = biomeAssets[(ci * 3 + i) % biomeAssets.length];
        const sp = textureSprite(asset.path, palette.biomeTint, stampSize(r, asset.defaultScale, 'biome'));
        // Local position within continent
        const angle = seeded() * Math.PI * 2;
        const dist = 0.08 + seeded() * 0.2;
        sp.x = Math.cos(angle) * r * dist;
        sp.y = Math.sin(angle) * r * dist * 0.6;
        sp.rotation = seeded() * Math.PI * 2;
        if (asset.canTint) sp.tint = palette.biomeTint;
        (cont.children[1] as PIXI.Container).addChild(sp); // localBiome
      }
    });

    // === DIRECT TERRAFORMING PATCHES ===
    const volcanicEffects: Array<{
      magma: PIXI.Graphics;
      smoke: Array<{ puff: PIXI.Graphics; baseX: number }>;
      phase: number;
    }> = [];

    const renderPlanetSurfaceAsset = (item: SurfaceItem, assetInfo: NonNullable<ReturnType<typeof mapUpgradeToAsset>>, asset: ReturnType<typeof pickMappedAssets>[number]) => {
      const surface = surfaceItemToXY(cx, cy, r, item);
      const cont = new PIXI.Container();
      cont.x = surface.x;
      cont.y = surface.y;
      cont.rotation = surface.angleRad + Math.PI / 2 + (item.layer === 'water' ? 0.04 : 0);
      const itemScale = clamp(Number(item.scale || 1), 0.8, 1.4);
      const isIce = assetInfo.idPrefix === 'subsurface_ice';
      const size = stampSize(r, asset.defaultScale, 'development') * itemScale * (isIce ? 0.72 : 1);
      cont.scale.set(visualScale(0.9, surface.radial), visualScale(0.9, surface.radial) * surfaceForeshortening(surface.radial));

      const sp = textureSprite(asset.path, assetInfo.category === 'water' ? 0x7edaf2 : 0xc5a66a, size);
      if (asset.canTint && assetInfo.category === 'water') sp.tint = palette.waterColor;
      if (isIce) {
        // Bright white ice stamps dominate the planet face — blend them into the
        // ocean palette and fade them out as the world warms through the stages.
        sp.tint = mixColor(palette.waterColor, 0xffffff, 0.45);
        cont.alpha = Math.max(0.18, 0.55 - stage * 0.08);
      }
      cont.addChild(sp);

      if (item.upgrade?.name === 'Volcanic Activity') {
        const magma = new PIXI.Graphics();
        magma.blendMode = PIXI.BLEND_MODES.ADD;
        magma.beginFill(0xff5f42, 0.25);
        magma.drawEllipse(0, size * 0.12, size * 0.32, size * 0.08);
        magma.endFill();
        cont.addChildAt(magma, 0);

        const smoke: Array<{ puff: PIXI.Graphics; baseX: number }> = [];
        for (let i = 0; i < 5; i++) {
          const puff = new PIXI.Graphics();
          puff.beginFill(0xecf0f1, 0.16);
          puff.drawCircle(0, 0, size * (0.035 + i * 0.004));
          puff.endFill();
          puff.x = (i - 2) * size * 0.035;
          puff.y = -size * (0.27 + i * 0.035);
          cont.addChild(puff);
          smoke.push({ puff, baseX: puff.x });
        }
        volcanicEffects.push({ magma, smoke, phase: (hash(item.item_id) % 1000) / 1000 });
      }

      let targetLayer: PIXI.Container;
      if (assetInfo.category === 'geology') {
        targetLayer = geologyLayer;
        cont.alpha = Math.max(0.22, 0.88 - stage * 0.14);
      } else if (assetInfo.category === 'water') {
        targetLayer = waterLayer;
      } else {
        targetLayer = developmentLayer;
      }
      targetLayer.addChild(cont);
    };

    // === DEVELOPMENT (settlements) mapped from surfaceItems ===
    visibleSurfaceItems.forEach((item) => {
      const name = item.upgrade?.name || '';
      const category = item.upgrade?.category;
      const assetInfo = mapUpgradeToAsset(name, category, stage);
      if (!assetInfo) return;

      if (assetInfo.category === 'continent') return;
      if (assetInfo.category === 'satellite') return;

      const assets = pickMappedAssets(assetInfo);
      if (assets.length === 0) return;
      const hasSurfaceVariants = assets.some((candidate) => candidate.placement.startsWith('planetSurface'));
      const asset = assets[(hasSurfaceVariants ? hash(item.item_id) : assetInfo.variant) % assets.length];

      if (asset.placement.startsWith('planetSurface') || (contRoots.length === 0 && (assetInfo.category === 'geology' || assetInfo.category === 'water'))) {
        renderPlanetSurfaceAsset(item, assetInfo, asset);
        return;
      }

      // Pick a continent based on item hash
      const contIdx = hash(item.item_id) % Math.max(1, contRoots.length);
      const cont = contRoots[contIdx];
      if (!cont) return;

      const itemScale = clamp(Number(item.scale || 1), 0.8, 1.4);
      const kind = mappedAssetKind(assetInfo.category);
      const sp = textureSprite(asset.path, 0xc5a66a, stampSize(r, asset.defaultScale, kind) * itemScale);
      const angle = (hash(item.item_id + '_dev') / 4294967296) * Math.PI * 2;
      const dist = 0.06 + (hash(item.item_id + '_d2') / 4294967296) * 0.18;
      sp.x = Math.cos(angle) * r * dist;
      sp.y = Math.sin(angle) * r * dist * 0.6;
      sp.rotation = (seeded() - 0.5) * 0.3;
      const targetLayerIndex = kind === 'biome' ? 1 : kind === 'road' ? 3 : kind === 'infrastructure' ? 4 : 2;
      (cont.children[targetLayerIndex] as PIXI.Container).addChild(sp);
    });

    // === ROADS ===
    const roadAssets = pickAssets(stage, 'road');
    const roadsOn = hasRoadNetwork;
    if (roadsOn && roadAssets.length > 0) {
      contRoots.forEach((cont) => {
        const roadAsset = roadAssets[0];
        const sp = textureSprite(roadAsset.path, 0x6d4c41, stampSize(r, roadAsset.defaultScale, 'road'));
        sp.x = 0;
        sp.y = r * 0.02;
        sp.rotation = 0.1;
        (cont.children[3] as PIXI.Container).addChild(sp); // localRoad
        if (hasRoadNetworkUpgrade && roadAssets.length > 1) {
          const extra = textureSprite(roadAssets[1].path, 0x6d4c41, stampSize(r, roadAssets[1].defaultScale, 'road') * 0.88);
          extra.x = r * 0.04;
          extra.y = -r * 0.03;
          extra.rotation = -0.16;
          (cont.children[3] as PIXI.Container).addChild(extra);
        }
      });
    }

    // === INFRASTRUCTURE ===
    const infraAssets = pickAssets(stage, 'infrastructure');
    if (hasInfrastructure && infraAssets.length > 0 && contRoots.length > 0) {
      const cont = contRoots[0];
      const asset = infraAssets[0];
      const sp = textureSprite(asset.path, 0x9e9e9e, stampSize(r, asset.defaultScale, 'infrastructure'));
      sp.x = r * 0.03;
      sp.y = r * 0.02;
      (cont.children[4] as PIXI.Container).addChild(sp); // localInfra
    }

    if ((hasLaunchTower || hasSpacePort || hasRocket) && contRoots.length > 0) {
      const cont = contRoots[0];
      const launch = new PIXI.Container();
      const tower = new PIXI.Graphics();
      tower.beginFill(0xb8c4d8, 0.95);
      tower.drawRect(-3, -18, 6, 28);
      tower.endFill();
      tower.lineStyle(1, 0xfff1a8, 0.8);
      tower.moveTo(-10, 10);
      tower.lineTo(10, 10);
      tower.moveTo(-8, 5);
      tower.lineTo(8, -10);
      launch.addChild(tower);
      if (hasRocket) {
        const rocketAssets = pickAssets(stage, 'rocket');
        if (rocketAssets.length > 0) {
          const rocketAsset = rocketAssets[0];
          const rocket = textureSprite(rocketAsset.path, 0xffffff, stampSize(r, rocketAsset.defaultScale, 'infrastructure') * 1.16);
          rocket.x = 0;
          rocket.y = -r * 0.08;
          rocket.rotation = -0.04;
          launch.addChild(rocket);
        } else {
          const rocket = new PIXI.Graphics();
          rocket.beginFill(0xf3f4f6, 0.98);
          rocket.drawRoundedRect(-4, -28, 8, 22, 4);
          rocket.endFill();
          rocket.beginFill(0xff7043, 0.95);
          rocket.moveTo(-4, -28);
          rocket.lineTo(0, -38);
          rocket.lineTo(4, -28);
          rocket.endFill();
          rocket.beginFill(0xffc247, 0.45);
          rocket.drawEllipse(0, -2, 5, 12);
          rocket.endFill();
          launch.addChild(rocket);
        }
      }
      launch.x = -r * 0.08;
      launch.y = -r * 0.01;
      launch.scale.set(hasSpacePort ? 1.18 : 1);
      (cont.children[4] as PIXI.Container).addChild(launch);
    }

    // === LIGHTS OVERLAY ===
    const lightsAssets = pickAssets(stage, 'overlay');
    const lightsOverlay = lightsAssets.find((a) => a.id.includes('lights'));
    if (lightsOverlay && hasCity && contRoots.length > 0) {
      contRoots.forEach((cont, ci) => {
        const asset = lightsAssets[(ci * 2) % lightsAssets.length];
        const sprite = textureSprite(asset.path, 0xffc86b, stampSize(r, 0.2, 'development'));
        sprite.x = (ci % 2 === 0 ? -1 : 1) * r * 0.07;
        sprite.y = r * 0.03;
        sprite.alpha = 0.75;
        sprite.blendMode = PIXI.BLEND_MODES.ADD;
        (cont.children[5] as PIXI.Container).addChild(sprite); // localLights
      });
    } else if (hasCity) {
      // Fallback radial glow
      const lights = new PIXI.Graphics();
      lights.beginFill(0xffc86b, 0.12);
      lights.drawCircle(cx, cy, r * 0.96);
      lights.endFill();
      lights.blendMode = PIXI.BLEND_MODES.ADD;
      lightsLayer.addChild(lights);
    }

    // === TRANSPORT HUBS AND ROUTES ===
    const portAssets = infraAssets.filter((a) => a.id.startsWith('port'));
    const airportAssets = infraAssets.filter((a) => a.id.startsWith('airport'));
    const boatAssets = pickAssets(stage, 'boat');
    const planeAssets = pickAssets(stage, 'plane');
    const hasPorts = dockPoints.length >= 2 && (hasSettlementTrading || hasSpacePort);
    const hasAirports = airportPoints.length >= 2 && (hasCity || hasLaunchTower || hasSpacePort);

    if (hasPorts) {
      dockPoints.forEach((dock, i) => {
        if (portAssets.length > 0) {
          const asset = portAssets[i % portAssets.length];
          const sp = textureSprite(asset.path, 0xbad7e8, r * 0.095);
          sp.position.set(dock.x, dock.y);
          sp.rotation = Math.atan2(dock.y - cy, dock.x - cx) + Math.PI / 2;
          infrastructureLayer.addChild(sp);
        } else {
          const port = new PIXI.Graphics();
          port.beginFill(0xd4b06f, 0.9);
          port.drawRoundedRect(-5, -3, 10, 6, 2);
          port.endFill();
          port.position.set(dock.x, dock.y);
          port.rotation = Math.atan2(dock.y - cy, dock.x - cx);
          infrastructureLayer.addChild(port);
        }
      });
    }

    if (hasAirports) {
      airportPoints.forEach((airport, i) => {
        if (airportAssets.length > 0) {
          const asset = airportAssets[i % airportAssets.length];
          const sp = textureSprite(asset.path, 0xe7edf4, r * 0.105);
          sp.position.set(airport.x, airport.y);
          sp.rotation = (seeded() - 0.5) * 0.5;
          infrastructureLayer.addChild(sp);
        } else {
          const runway = new PIXI.Graphics();
          runway.beginFill(0xdce7f0, 0.9);
          runway.drawRoundedRect(-9, -2, 18, 4, 1.5);
          runway.endFill();
          runway.position.set(airport.x, airport.y);
          runway.rotation = (seeded() - 0.5) * 0.5;
          infrastructureLayer.addChild(runway);
        }
      });
    }

    const makeTransport = (mode: 'plane' | 'boat' | 'vehicle', color: number) => {
      const g = new PIXI.Graphics();
      g.beginFill(color, 0.95);
      if (mode === 'plane') {
        g.moveTo(7, 0);
        g.lineTo(-5, -3);
        g.lineTo(-2, 0);
        g.lineTo(-5, 3);
        g.closePath();
        g.drawRect(-1, -1, 5, 2);
      } else if (mode === 'boat') {
        g.drawRoundedRect(-6, -2, 12, 4, 1.5);
        g.beginFill(0xffffff, 0.8);
        g.moveTo(-1, -2);
        g.lineTo(3, -8);
        g.lineTo(5, -2);
        g.closePath();
      } else {
        g.drawCircle(0, 0, 3);
      }
      g.endFill();
      return g;
    };
    const makeBoatRoute = (from: PIXI.Point, to: PIXI.Point, routeIndex: number) => {
      const startAngle = Math.atan2(from.y - cy, from.x - cx);
      const endAngle = Math.atan2(to.y - cy, to.x - cx);
      let delta = angleDelta(startAngle, endAngle);
      if (Math.abs(delta) < 0.55) delta += delta >= 0 ? 0.75 : -0.75;
      const radius = r * (0.76 + (routeIndex % 3) * 0.045);
      const points = [from];
      for (let i = 1; i < 12; i++) {
        const t = i / 12;
        const angle = startAngle + delta * t;
        points.push(new PIXI.Point(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius));
      }
      points.push(to);
      return points;
    };
    const makePlaneRoute = (from: PIXI.Point, to: PIXI.Point, routeIndex: number) => {
      const mx = (from.x + to.x) / 2;
      const my = (from.y + to.y) / 2;
      const awayX = mx - cx;
      const awayY = my - cy;
      const awayLen = Math.max(1, Math.hypot(awayX, awayY));
      const lift = r * (0.14 + (routeIndex % 2) * 0.08);
      const control = new PIXI.Point(mx + (awayX / awayLen) * lift, my + (awayY / awayLen) * lift);
      const points: PIXI.Point[] = [];
      for (let i = 0; i <= 14; i++) {
        const t = i / 14;
        const mt = 1 - t;
        points.push(new PIXI.Point(
          mt * mt * from.x + 2 * mt * t * control.x + t * t * to.x,
          mt * mt * from.y + 2 * mt * t * control.y + t * t * to.y,
        ));
      }
      return points;
    };
    const drawRoute = (points: PIXI.Point[], color: number, alpha: number) => {
      if (points.length < 2) return;
      const line = new PIXI.Graphics();
      line.lineStyle(1.3, color, alpha);
      line.moveTo(points[0].x, points[0].y);
      for (const point of points.slice(1)) line.lineTo(point.x, point.y);
      routeLayer.addChild(line);
    };
    const movers: RouteMover[] = [];
    const addMover = (sp: PIXI.DisplayObject, points: PIXI.Point[], speed: number, offset: number) => {
      const metrics = routeMetrics(points);
      if (metrics.totalLength <= 0) return;
      movementLayer.addChild(sp);
      movers.push({
        sp,
        points,
        segmentLengths: metrics.segmentLengths,
        totalLength: metrics.totalLength,
        t: offset,
        speed,
      });
    };

    if (hasPorts) {
      const routeCount = Math.min(dockPoints.length, hasCity ? 5 : 3);
      for (let i = 0; i < routeCount; i++) {
        const from = dockPoints[i % dockPoints.length];
        const to = dockPoints[(i + 1 + (i % Math.max(1, dockPoints.length - 1))) % dockPoints.length];
        const route = makeBoatRoute(from, to, i);
        drawRoute(route, 0x7edaf2, 0.14);
        const asset = boatAssets[i % Math.max(1, boatAssets.length)];
        const sp = asset ? textureSprite(asset.path, 0xfff1a8, r * 0.11) : makeTransport('boat', 0xfff1a8);
        sp.alpha = 0.92;
        addMover(sp, route, 0.00018 + seeded() * 0.00012, seeded());
      }
    }

    if (hasAirports) {
      const routeCount = Math.min(airportPoints.length, 4);
      for (let i = 0; i < routeCount; i++) {
        const from = airportPoints[i % airportPoints.length];
        const to = airportPoints[(i + 2) % airportPoints.length];
        const route = makePlaneRoute(from, to, i);
        drawRoute(route, 0xc7e8ff, 0.1);
        const asset = planeAssets[i % Math.max(1, planeAssets.length)];
        const sp = asset ? textureSprite(asset.path, 0x8bdcff, r * 0.12) : makeTransport('plane', 0x8bdcff);
        sp.alpha = 0.88;
        addMover(sp, route, 0.00024 + seeded() * 0.00013, seeded());
      }
    }

    if (!hasPorts && !hasAirports && hasEarlySettlement) {
      for (const point of dockPoints.slice(0, 3)) {
        const dot = new PIXI.Graphics();
        dot.beginFill(0xffffff, 0.75);
        dot.drawCircle(0, 0, 2.2);
        dot.endFill();
        dot.position.set(point.x, point.y);
        movementLayer.addChild(dot);
      }
    }

    // === CLOUDS & ATMOSPHERE ===
    let cloudSprite: PIXI.Sprite | null = null;
    let rainDrops: PIXI.Graphics[] = [];
    if (stage >= 1 || hasWaterUnlock) {
      const cloudAsset = lightsAssets.find((a) => a.id.includes('cloud'));
      if (cloudAsset) {
        cloudSprite = textureSprite(cloudAsset.path, 0xffffff, r * 2.02);
      } else {
        // Fallback soft cloud circle
        const cg = new PIXI.Graphics();
        cg.beginFill(0xffffff, 0.08);
        cg.drawCircle(0, 0, r * 1.1);
        cg.endFill();
        cloudSprite = new PIXI.Sprite(app.renderer.generateTexture(cg));
        cloudSprite.anchor.set(0.5);
        cloudSprite.width = r * 2.02;
        cloudSprite.height = r * 2.02;
      }
      cloudSprite.x = cx;
      cloudSprite.y = cy;
      cloudSprite.alpha = kinds.storm ? 0.16 : 0.08;
      cloudLayer.addChild(cloudSprite);

      // Atmosphere glow
      const atmoAsset = lightsAssets.find((a) => a.id.includes('atmosphere'));
      if (atmoAsset) {
        const atmo = textureSprite(atmoAsset.path, palette.atmosphereColor, r * 2.22);
        atmo.x = cx;
        atmo.y = cy;
        atmo.alpha = atmOpacity;
        atmo.blendMode = PIXI.BLEND_MODES.ADD;
        atmosphereLayer.addChild(atmo);
      } else {
        const atmo = new PIXI.Graphics();
        atmo.beginFill(palette.atmosphereColor, atmOpacity * 0.5);
        atmo.drawCircle(cx, cy, r * 1.12);
        atmo.endFill();
        atmo.blendMode = PIXI.BLEND_MODES.ADD;
        atmosphereLayer.addChild(atmo);
      }

      // Rain during storms
      if (kinds.storm || kinds.tsunami) {
        for (let i = 0; i < 40; i++) {
          const d = new PIXI.Graphics();
          d.lineStyle(1, 0xaad8ff, 0.35);
          d.moveTo(0, 0);
          d.lineTo(0, 8 + seeded() * 8);
          d.x = cx - r + seeded() * r * 2;
          d.y = cy - r + seeded() * r * 2;
          cloudLayer.addChild(d);
          rainDrops.push(d);
        }
      }
    }

    // === EVENT OVERLAYS ===
    const quakeRing = new PIXI.Graphics();
    if (kinds.earthquake) {
      quakeRing.lineStyle(3, 0xffb74d, 0.45);
      quakeRing.drawCircle(cx, cy, r * 0.9);
      atmosphereLayer.addChild(quakeRing);
    }

    const flareOverlay = new PIXI.Graphics();
    if (kinds.solarFlare || hasSolarFlare) {
      flareOverlay.beginFill(0xffa726, 0.18);
      flareOverlay.drawCircle(cx, cy, r * 1.15);
      flareOverlay.endFill();
      flareOverlay.blendMode = PIXI.BLEND_MODES.ADD;
      atmosphereLayer.addChild(flareOverlay);
    }

    const tsunamiArc = new PIXI.Graphics();
    // Pivot at the planet centre so the ticker's rotation orbits the planet
    // rather than swinging the arc around the canvas origin.
    tsunamiArc.pivot.set(cx, cy);
    tsunamiArc.position.set(cx, cy);
    if (kinds.tsunami) {
      tsunamiArc.lineStyle(4, 0x4fc3f7, 0.5);
      tsunamiArc.arc(cx, cy, r * 0.88, Math.PI * 0.1, Math.PI * 0.9);
      waterLayer.addChild(tsunamiArc);
    }

    if (kinds.pandemic || hasPandemic) {
      const healthHaze = new PIXI.Graphics();
      healthHaze.beginFill(0x8bd17c, 0.08);
      healthHaze.drawCircle(cx, cy, r * 0.92);
      healthHaze.endFill();
      healthHaze.blendMode = PIXI.BLEND_MODES.ADD;
      atmosphereLayer.addChild(healthHaze);
    }

    if (hasMeteorImpact) {
      const impact = new PIXI.Graphics();
      impact.lineStyle(2, 0xff7043, 0.55);
      impact.moveTo(cx - r * 0.56, cy - r * 0.48);
      impact.lineTo(cx - r * 0.2, cy - r * 0.12);
      atmosphereLayer.addChild(impact);
    }

    // === ASTEROID BELT ===
    // Asteroid count is fixed at planet creation: 70% chance of 7-20, 30% chance of 0.
    // Legacy planets use asteroidBelt; new planets use asteroidCount.
    const asteroidCount =
      typeof config.asteroidCount === 'number'
        ? config.asteroidCount
        : config.asteroidBelt === 'none'
          ? 0
          : config.asteroidBelt === 'sparse'
            ? 8
            : config.asteroidBelt === 'broken'
              ? 15
              : config.asteroidBelt === 'dense'
                ? 22
                : config.asteroidBelt === 'cluster'
                  ? 10
                  : 0;
    if (asteroidCount > 0) {
      const cnt = asteroidCount;
      const ast: { g: PIXI.Sprite | PIXI.Graphics; a: number; o: number; s: number; rot: number; front: boolean }[] = [];
      const asteroidAssets = pickAssets(0, 'asteroid');

      for (let i = 0; i < cnt; i++) {
        let g: PIXI.Sprite | PIXI.Graphics;
        if (asteroidAssets.length > 0) {
          const asset = asteroidAssets[i % asteroidAssets.length];
          g = textureSprite(asset.path, 0x8d6e63, r * asset.defaultScale * (0.58 + seeded() * 0.62));
        } else {
          g = new PIXI.Graphics();
          g.beginFill(0x8d6e63);
          const points = Array.from({ length: 8 }, (_, pointIndex) => {
            const angle = (Math.PI * 2 * pointIndex) / 8 + (seeded() - 0.5) * 0.35;
            const radius = 7 + seeded() * 7;
            return new PIXI.Point(Math.cos(angle) * radius, Math.sin(angle) * radius);
          });
          g.drawPolygon(points);
          g.endFill();
        }
        asteroidBackLayer.addChild(g);
        const speed = 0.00022 + seeded() * 0.00055;
        ast.push({
          g,
          a: (seeded() * Math.PI * 2) + ((Date.now() - stableEpochMs) / (1000 / 60)) * speed,
          o: r * (1.16 + seeded() * 0.34),
          s: speed,
          rot: (seeded() - 0.5) * 0.018,
          front: false,
        });
      }

      app.ticker.add((d) =>
        ast.forEach((x) => {
          x.a += x.s * d;
          const px = cx + Math.cos(x.a) * x.o;
          const py = cy + Math.sin(x.a) * x.o * 0.72;
          x.g.x = px;
          x.g.y = py;
          x.g.rotation += x.rot * d;

          const inFrontHemisphere = py > cy;
          if (inFrontHemisphere !== x.front) {
            x.front = inFrontHemisphere;
            if (x.front) {
              asteroidBackLayer.removeChild(x.g);
              asteroidFrontLayer.addChild(x.g);
            } else {
              asteroidFrontLayer.removeChild(x.g);
              asteroidBackLayer.addChild(x.g);
            }
          }
          x.g.alpha = x.front ? 0.92 : 0.34;
        }),
      );
    }

    const orbiters: { body: PIXI.DisplayObject; angle: number; radius: number; speed: number; yScale: number }[] = [];
    const shieldSprites: { sprite: PIXI.Sprite; baseScale: number }[] = [];
    const hasSpaceTech = hasSatellite || hasOrbitalDock || hasProbe || hasDefenceSatellites || hasShieldSatellites;
    const satelliteAssets = pickAssets(stage, 'satellite');
    if (hasSpaceTech) {
      // Low orbit: max 2 satellites
      const lowOrbitCount = Math.min(2, Math.max(1, purchasedNames.filter((name) =>
        ['Satellite', 'Orbital Telescope', 'Orbital Dock', 'Deep Space Probe', 'Orbital Defence Platform'].includes(name)
      ).length));
      for (let i = 0; i < lowOrbitCount; i++) {
        let body: PIXI.DisplayObject;
        if (satelliteAssets.length > 0) {
          const asset = satelliteAssets[i % satelliteAssets.length];
          body = textureSprite(asset.path, 0xdce7ff, r * 0.13);
        } else {
          const fallback = new PIXI.Container();
          const core = new PIXI.Graphics();
          core.beginFill(0xdce7ff, 0.95);
          core.drawRoundedRect(-3, -2, 6, 4, 1);
          core.endFill();
          const panels = new PIXI.Graphics();
          panels.beginFill(0x5aa9ff, 0.8);
          panels.drawRect(-12, -1, 8, 2);
          panels.drawRect(4, -1, 8, 2);
          panels.endFill();
          fallback.addChild(panels, core);
          body = fallback;
        }
        asteroidFrontLayer.addChild(body);
        const speed = 0.00046 + seeded() * 0.00034 + (hasDefenceSatellites ? 0.00008 : 0);
        orbiters.push({
          body,
          angle: (seeded() * Math.PI * 2) + ((Date.now() - stableEpochMs) / (1000 / 60)) * speed,
          radius: r * (1.05 + i * 0.12),
          speed,
          yScale: 0.64 + seeded() * 0.12,
        });
      }
      // High orbit: 1 satellite only after shield satellites researched
      if (hasShieldSatellites) {
        let body: PIXI.DisplayObject;
        if (satelliteAssets.length > 0) {
          const asset = satelliteAssets[0];
          body = textureSprite(asset.path, 0x9fd7ff, r * 0.11);
          if (body instanceof PIXI.Sprite) body.tint = 0x9fd7ff;
        } else {
          const fallback = new PIXI.Container();
          const core = new PIXI.Graphics();
          core.beginFill(0x9fd7ff, 0.95);
          core.drawRoundedRect(-3, -2, 6, 4, 1);
          core.endFill();
          const panels = new PIXI.Graphics();
          panels.beginFill(0x5aa9ff, 0.8);
          panels.drawRect(-12, -1, 8, 2);
          panels.drawRect(4, -1, 8, 2);
          panels.endFill();
          fallback.addChild(panels, core);
          body = fallback;
        }
        asteroidFrontLayer.addChild(body);
        orbiters.push({
          body,
          angle: (seeded() * Math.PI * 2) + ((Date.now() - stableEpochMs) / (1000 / 60)) * 0.00028,
          radius: r * 1.58,
          speed: 0.00028,
          yScale: 0.58,
        });
      }
    }

    // === PLANETARY DEFENCE QUARTER DOME ===
    if (hasDefenceSatellites || hasShieldSatellites) {
      const shieldAssets = pickAssets(stage, 'overlay').filter((a) => a.id.includes('shield') || a.id.includes('dome'));
      if (shieldAssets.length > 0) {
        const shieldSp = textureSprite(shieldAssets[0].path, 0x4fc3f7, r * 0.9);
        shieldSp.position.set(cx - r * 0.75, cy - r * 0.55);
        shieldSp.alpha = 0.32;
        shieldSp.blendMode = PIXI.BLEND_MODES.ADD;
        asteroidFrontLayer.addChild(shieldSp);
        shieldSprites.push({ sprite: shieldSp, baseScale: shieldSp.scale.x });
      } else {
        // Fallback quarter dome
        const dome = new PIXI.Graphics();
        dome.blendMode = PIXI.BLEND_MODES.ADD;
        dome.lineStyle(2, 0x4fc3f7, 0.25);
        dome.arc(cx - r * 0.2, cy - r * 0.2, r * 0.85, Math.PI, Math.PI * 1.5);
        dome.lineTo(cx - r * 0.2, cy - r * 0.2);
        dome.beginFill(0x4fc3f7, 0.06);
        dome.arc(cx - r * 0.2, cy - r * 0.2, r * 0.85, Math.PI, Math.PI * 1.5);
        dome.lineTo(cx - r * 0.2, cy - r * 0.2);
        dome.endFill();
        asteroidFrontLayer.addChild(dome);
      }
    }

    // === SPACE DOCKING STATION (static, bottom-left) ===
    const dockStationRoutes: PIXI.Point[] = [];
    if (hasOrbitalDock) {
      const dockAssets = pickAssets(stage, 'satellite').filter((a) => a.id.includes('space_station_dock'));
      const stationX = cx - r * 0.82;
      const stationY = cy + r * 0.58;
      if (dockAssets.length > 0) {
        const dockSp = textureSprite(dockAssets[0].path, 0xdce7ff, r * 0.24);
        dockSp.position.set(stationX, stationY);
        asteroidFrontLayer.addChild(dockSp);
      } else {
        const station = new PIXI.Container();
        const platform = new PIXI.Graphics();
        platform.beginFill(0xc5d1e8, 0.9);
        platform.drawRoundedRect(-14, -6, 28, 12, 3);
        platform.endFill();
        platform.beginFill(0x8fa4c8, 0.7);
        platform.drawRect(-4, -14, 8, 8);
        platform.endFill();
        station.addChild(platform);
        station.position.set(stationX, stationY);
        asteroidFrontLayer.addChild(station);
      }
      // Build a route from nearest dock point to station for supply ship
      const nearestDock = dockPoints.length > 0 ? dockPoints[0] : new PIXI.Point(cx + r * 0.3, cy + r * 0.3);
      const midX = (nearestDock.x + stationX) / 2 + r * 0.15;
      const midY = (nearestDock.y + stationY) / 2 - r * 0.1;
      for (let i = 0; i <= 16; i++) {
        const t = i / 16;
        const mt = 1 - t;
        dockStationRoutes.push(new PIXI.Point(
          mt * mt * nearestDock.x + 2 * mt * t * midX + t * t * stationX,
          mt * mt * nearestDock.y + 2 * mt * t * midY + t * t * stationY,
        ));
      }
    }

    // === SUPPLY SHIP (between planet and dock station) ===
    let supplyShipMover: RouteMover | null = null;
    if (hasOrbitalDock && dockStationRoutes.length > 0) {
      const shipAssets = pickAssets(stage, 'movement').filter((a) => a.id.includes('ship') || a.id.includes('supply'));
      let shipSp: PIXI.DisplayObject;
      if (shipAssets.length > 0) {
        shipSp = textureSprite(shipAssets[0].path, 0xf0f4ff, r * 0.08);
      } else {
        const ship = new PIXI.Graphics();
        ship.beginFill(0xf0f4ff, 0.95);
        ship.drawRoundedRect(-5, -2, 10, 4, 1.5);
        ship.endFill();
        ship.beginFill(0x5aa9ff, 0.8);
        ship.drawRect(-2, -5, 4, 3);
        ship.endFill();
        shipSp = ship;
      }
      const metrics = routeMetrics(dockStationRoutes);
      if (metrics.totalLength > 0) {
        asteroidFrontLayer.addChild(shipSp);
        supplyShipMover = {
          sp: shipSp,
          points: dockStationRoutes,
          segmentLengths: metrics.segmentLengths,
          totalLength: metrics.totalLength,
          t: 0,
          speed: 0.00022,
        };
      }
    }

    // === TRANSPORT SIZING ===
    // (handled inline below where boats/planes are created)

    // === ANIMATION TICKER ===
    app.ticker.add((d) => {
      planetContainer.rotation += rotationSpeed * d;
      const now = Date.now();
      const phase = (((now - stableEpochMs) % visualCycleMs) + visualCycleMs) % visualCycleMs / visualCycleMs;
      if (phase < 0.5) {
        const p = phase / 0.5;
        driftingStar.visible = true;
        const arc = Math.sin(p * Math.PI);
        driftingStar.x = -w * 0.16 + w * 1.32 * p;
        driftingStar.y = cy - r * 0.42 - arc * r * 0.16 + Math.sin(p * Math.PI * 2) * r * 0.025;
        driftingStar.alpha = arc * 0.82;
        driftingStar.scale.set(0.82 + arc * 0.1);
        frontLightLayer.visible = false;
      } else {
        const p = (phase - 0.5) / 0.5;
        driftingStar.visible = false;
        frontLightLayer.visible = true;
        frontLight.x = cx + (0.5 - p) * r * 0.95;
        frontLight.y = cy - r * 0.08 - Math.sin(p * Math.PI) * r * 0.045;
        frontLight.alpha = 0.04 + Math.sin(p * Math.PI) * 0.16;
      }

      stars.forEach((star) => {
        star.g.alpha = clamp(star.base + Math.sin(now * star.speed + star.phase) * 0.11, 0.03, 0.5);
      });

      // Movement interpolation
      movers.forEach((m) => {
        m.t += m.speed * d;
        if (m.t > 1) m.t = 0;
        const current = pointAlongRoute(m, m.t);
        const next = pointAlongRoute(m, m.t + 0.004);
        m.sp.x = current.x;
        m.sp.y = current.y;
        m.sp.rotation = Math.atan2(next.y - current.y, next.x - current.x);
      });

      // Supply ship animation (dock station to planet and back)
      if (supplyShipMover) {
        supplyShipMover.t += supplyShipMover.speed * d;
        if (supplyShipMover.t > 1) supplyShipMover.t = 0;
        const current = pointAlongRoute(supplyShipMover, supplyShipMover.t);
        const next = pointAlongRoute(supplyShipMover, supplyShipMover.t + 0.004);
        supplyShipMover.sp.x = current.x;
        supplyShipMover.sp.y = current.y;
        supplyShipMover.sp.rotation = Math.atan2(next.y - current.y, next.x - current.x);
      }

      orbiters.forEach((orbiter) => {
        orbiter.angle += orbiter.speed * d;
        const x = cx + Math.cos(orbiter.angle) * orbiter.radius;
        const y = cy + Math.sin(orbiter.angle) * orbiter.radius * orbiter.yScale;
        orbiter.body.position.set(x, y);
        orbiter.body.rotation = orbiter.angle + Math.PI / 2;
        orbiter.body.alpha = y > cy ? 0.95 : 0.45;
        const targetLayer = y > cy ? asteroidFrontLayer : asteroidBackLayer;
        if (orbiter.body.parent !== targetLayer) {
          orbiter.body.parent?.removeChild(orbiter.body);
          targetLayer.addChild(orbiter.body);
        }
      });

      shieldSprites.forEach(({ sprite: shield, baseScale }, i) => {
        const pulse = Math.sin(now * 0.0022 + i * 0.7);
        shield.alpha = 0.28 + pulse * 0.055;
        shield.scale.set(baseScale * (1 + pulse * 0.012));
      });

      // Light pulse
      lightsLayer.alpha = 0.45 + Math.sin(Date.now() * 0.002) * 0.08;

      // Cloud drift
      cloudLayer.rotation += (kinds.storm ? 0.00045 : 0.00015) * d;
      if (cloudSprite) cloudSprite.alpha = (kinds.storm ? 0.14 : 0.08) + Math.sin(Date.now() * 0.0015) * 0.02;

      waterFlowLines.forEach((line, index) => {
        line.alpha = 0.65 + Math.sin(Date.now() * 0.0012 + index * 0.7) * 0.18;
        line.x = Math.sin(Date.now() * 0.00045 + index) * r * 0.012;
      });
      rippleBands.forEach((band, index) => {
        band.rotation += (0.00045 + index * 0.00008) * d;
        band.alpha = 0.55 + Math.sin(Date.now() * 0.0014 + index) * 0.18;
        band.scale.set(1 + Math.sin(Date.now() * 0.001 + index) * 0.025);
      });

      volcanicEffects.forEach((effect) => {
        const pulse = 0.5 + Math.sin(Date.now() * 0.004 + effect.phase * Math.PI * 2) * 0.5;
        effect.magma.alpha = 0.45 + pulse * 0.28;
        effect.smoke.forEach(({ puff, baseX }, index) => {
          const drift = (Date.now() * 0.00018 + effect.phase + index * 0.18) % 1;
          puff.y = -r * (0.08 + drift * 0.08 + index * 0.006);
          puff.x = baseX + Math.sin(Date.now() * 0.001 + index) * r * 0.012;
          puff.alpha = 0.22 * (1 - drift);
          const scale = 0.85 + drift * 1.15;
          puff.scale.set(scale);
        });
      });

      // Rain drops
      for (const drop of rainDrops) {
        drop.y += (2.2 + (drop.x % 3)) * d;
        if (drop.y > cy + r) drop.y = cy - r;
      }

      // Event animations
      if (kinds.earthquake) {
        planetContainer.position.set(
          cx + Math.sin(Date.now() * 0.05) * 1.8,
          cy + Math.cos(Date.now() * 0.04) * 1.2,
        );
      } else {
        planetContainer.position.set(cx, cy);
      }
      if (kinds.solarFlare || hasSolarFlare) flareOverlay.alpha = 0.12 + Math.sin(Date.now() * 0.004) * 0.08;
      if (kinds.tsunami) tsunamiArc.rotation += 0.002 * d;

      // Subtle pulse on development items (buildings "breathing")
      const devPulse = 1 + Math.sin(Date.now() * 0.001) * 0.015;
      contRoots.forEach((cont) => {
        const localDev = cont.children[2] as PIXI.Container | undefined;
        if (localDev && localDev.scale) {
          localDev.scale.set(devPulse);
        }
      });
    });

    return () => {
      const view = app.view as HTMLCanvasElement | undefined;
      const parent = ref.current;
      app.ticker.stop();

      // Keep the previous frame visible briefly so rebuilds never expose a blank container.
      if (view && parent && view.parentNode === parent) {
        view.style.pointerEvents = 'none';
        view.style.zIndex = '0';
        window.setTimeout(() => {
          app.destroy(true, { children: true, texture: false, baseTexture: false });
          if (view.parentNode === parent) parent.removeChild(view);
        }, 180);
      } else {
        app.destroy(true, { children: true, texture: false, baseTexture: false });
      }
    };
  }, [
    stage,
    surfaceItemsKey,
    eventHistoryKey,
    rotationSpeed,
    visualState?.atmosphereOpacity,
    visualState?.waterOpacity,
    visualState?.atmosphereColor,
    visualState?.waterColor,
    visualState?.starColor,
    externalConfigKey,
    canvasSize.width,
    canvasSize.height,
    planetId,
    orbitEpochMs,
    dayDurationMs,
  ]);

  return <div ref={ref} className="relative w-full h-full min-h-0 overflow-hidden bg-slate-950" />;
}
